import apiClient from '@/lib/api';

export type LookupType =
  | 'auto'
  | 'agency'
  | 'subaccount'
  | 'form'
  | 'user'
  | 'lead'
  | 'submission'
  | 'appointment'
  | 'stripe_customer'
  | 'stripe_subscription'
  | 'ghl_location'
  | 'ghl_calendar'
  | 'calendly_event_type';

export type LookupKind =
  | 'agency' | 'subaccount' | 'form' | 'user' | 'lead' | 'submission' | 'appointment'
  | 'integration' | 'ending' | 'element' | 'webhook' | 'split_test';

export interface LookupRef {
  id: number;
  publicId: string;
  name: string;
}

export interface LookupField {
  label: string;
  value: string | null;
  mono?: boolean;
  date?: boolean;
}

export interface LookupMatch {
  kind: LookupKind;
  matchedBy: string;
  id: number;
  publicId: string | null;
  title: string;
  subtitle: string | null;
  path: { agency: LookupRef | null; subaccount: LookupRef | null; form: LookupRef | null };
  fields: LookupField[];
}

export interface LookupDetail {
  agency: {
    id: number; publicId: string; name: string; slug: string; status: string; plan: string | null;
    stripeStatus: string | null; stripeCustomerId: string | null; stripeSubscriptionId: string | null;
    createdAtUtc: string | null;
  } | null;
  subaccount: {
    id: number; publicId: string; name: string; status: string; formsCount: number; publishedFormsCount: number;
    createdAtUtc: string | null;
  } | null;
  form: {
    id: number; publicId: string; name: string; status: string; skin: string; completedSubmissions: number;
    lastCompletedAtUtc: string | null; createdAtUtc: string | null;
  } | null;
  /** Agency roles are super_admin (the owner) and admin; sub-account roles are owner and editor. */
  people: Array<{ id: number; publicId: string; name: string; email: string; role: string; scope: 'agency' | 'subaccount' }>;
  integrations: Array<{ provider: string; name: string; status: string; locationId: string | null }>;
  webhooks: { active: number; total: number } | null;
  calendarEndings: Array<{
    endingId: number; endingName: string; provider: string; calendarId: string | null;
    calendarStatus: string | null; calendarCheckedAtUtc: string | null; form: LookupRef;
  }>;
}

export interface LookupResult {
  matches: LookupMatch[];
  detail: LookupDetail | null;
  /** A list hit its limit of 20, so more records match than are shown. */
  truncated: boolean;
}

export const lookupService = {
  lookup: async (params: { q: string; type: LookupType; select?: string | null }): Promise<LookupResult> => {
    const search = new URLSearchParams({ q: params.q, type: params.type });
    if (params.select) search.set('select', params.select);
    const response = await apiClient.get(`/admin/lookup?${search.toString()}`);
    return response.data;
  },
};
